import React, { useEffect } from "react";
import getConfig from "next/config";

import { Caption } from "components/Caption";
import { MetadataObj } from "utils/objects";
import { genPilboxURL } from "utils/pilbox";
import { initLazyImages } from "utils/images";
import styles from "./responsiveImageModule.module.scss";

export interface ResponsiveImageProps {
    altText: string;
    url: string;
    aspectHeight?: number;
    aspectWidth?: number;
    className?: string;
    mobileWidth: number;
    tabletWidth?: number;
    laptopWidth?: number;
    desktopWidth?: number;
    bigScreenWidth?: number;
    mobileHeight?: number;
    tabletHeight?: number;
    laptopHeight?: number;
    desktopHeight?: number;
    bigScreenHeight?: number;
    round?: boolean;
    credit?: string;
    creditLink?: string;
    caption?: string;
    mode?: "scale" | "clip" | "crop" | "fill";
    loading?: "eager" | "lazy" | undefined;
    extraProps?: MetadataObj;
}

const pilboxConfig: MetadataObj = {
    filter: "antialias",
    mode: "scale",
    pos: "top-left",
    opt: 1,
    prog: 1,
    q: "keep"
};

/**
 * Generates the CDN URL of an image given its path and the CDN URL.
 */
const getCDNUrl = (path: string, media: string) => {
    const url = !/^http/.test(path) ? `${media}/${path}` : path;
    return `${url.replace("//media", "/media")}`;
};

/**
 * Provides a responsive image using srcSet, aspect ratios and pillbox.
 * @example
 * <ResponsiveImage
 *     alt="FluffyKins"
 *     className="jumbotron"
 *     url="//media/image.jpg"
 *     mobileWidth={320}
 *     tabletWidth={200}
 *     laptopWidth={162}
 *     desktopWidth={180}
 *     bigScreenWidth={206}
 *     mobileHeight={320}
 *     tabletHeight={200}
 *     laptopHeight={162}
 *     desktopHeight={180}
 *     bigScreenHeight={206}
 *     round{false}
 * />
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = (props) => {
    const { publicRuntimeConfig: { pilboxHost, staticAssetHost } } = getConfig();
    const {
        url,
        mobileWidth,
        tabletWidth,
        laptopWidth,
        desktopWidth,
        bigScreenWidth,
        mobileHeight,
        tabletHeight,
        laptopHeight,
        desktopHeight,
        bigScreenHeight,
        className,
        altText,
        credit,
        creditLink,
        caption,
        mode,
        loading,
        extraProps
    } = props;

    if (!url) {
        return null;
    }

    // Images should always have an alt text.
    const alt: string = altText || caption || credit || "";

    // always fall back to the next smaller size.
    const mobileW = mobileWidth;
    const tabletW = tabletWidth || mobileW;
    const laptopW = laptopWidth || tabletW;
    const desktopW = desktopWidth || laptopW;
    const bigScreenW = bigScreenWidth || desktopW;

    // always fall back to the next smaller size.
    const mobileH = mobileHeight;
    const tabletH = tabletHeight || mobileH;
    const laptopH = laptopHeight || tabletH;
    const desktopH = desktopHeight || laptopH;
    const bigScreenH = bigScreenHeight || desktopH;

    const cdnUrl = getCDNUrl(url, staticAssetHost);

    // Generate each breakpoints Pilbox URL.
    const xs = genPilboxURL(pilboxHost, cdnUrl, {
        ...pilboxConfig, mode, w: mobileW, h: mobileH
    });
    const sm = genPilboxURL(pilboxHost, cdnUrl, {
        ...pilboxConfig, mode, w: tabletW, h: tabletH
    });
    const md = genPilboxURL(pilboxHost, cdnUrl, {
        ...pilboxConfig, mode, w: laptopW, h: laptopH
    });
    const lg = genPilboxURL(pilboxHost, cdnUrl, {
        ...pilboxConfig, mode, w: desktopW, h: desktopH
    });
    const xl = genPilboxURL(pilboxHost, cdnUrl, {
        ...pilboxConfig, mode, w: bigScreenW, h: bigScreenH
    });

    // add some class names
    // eslint-disable-next-line react/destructuring-assignment
    const modifier = props.round ? `${styles.imageResponsive} ${styles.imageRound}` : styles.imageResponsive;
    const wrapperClass = [modifier, className].join(" ");

    // Building up the srcSet as an object so we remove duplicate sizes/paths
    const srcSet: MetadataObj = {};
    srcSet[`${mobileW}w`] = xs;
    srcSet[`${tabletW}w`] = srcSet[`${tabletW}w`] || sm;
    srcSet[`${laptopW}w`] = srcSet[`${laptopW}w`] || md;
    srcSet[`${desktopW}w`] = srcSet[`${desktopW}w`] || lg;
    srcSet[`${bigScreenW}w`] = srcSet[`${bigScreenW}w`] || xl;

    // Building up the sizes as an object so we remove duplicate sizes/breakpoints
    const sizes: MetadataObj = {};
    sizes[`${mobileW}px`] = "(max-width: 567px)";
    sizes[`${tabletW}px`] = sizes[`${tabletW}px`] || "(min-width: 567px)";
    sizes[`${laptopW}px`] = sizes[`${laptopW}px`] || "(min-width: 1024px)";
    sizes[`${desktopW}px`] = sizes[`${desktopW}px`] || "(min-width: 1200px)";
    sizes[`${bigScreenW}px`] = sizes[`${bigScreenW}px`] || "(min-width: 1600px)";

    // Function for mapping over the SrcSet.
    const getSrcSet = (key: any) => `${srcSet[key]} ${key}`;

    // loop over the srcset and join it all together `path size`
    const srcSetMap: string = Object.keys(srcSet)
        .map(getSrcSet)
        .join(",");

    // Function for mapping over the sizes.
    const getSize = (val: string, _i: number, self: string[]) => (val === self[self.length - 1] ? val : `${sizes[val]} ${val}`);

    // loop over the sizesMap and join it all together `breakpoint size`
    const sizesMap: string = Object.keys(sizes)
        .map(getSize)
        .join(",");

    const isEager = loading === "eager";

    useEffect(() => {
        initLazyImages();
    }, []);

    return (
        <>
            <img
                alt={alt}
                className={isEager ? wrapperClass : `${wrapperClass} lazy`}
                data-src={isEager ? undefined : xl}
                src={isEager ? xl : undefined}
                data-srcset={isEager ? undefined : srcSetMap}
                srcSet={isEager ? srcSetMap : undefined}
                loading={loading || undefined}
                sizes={sizesMap}
                width={bigScreenW}
                height={bigScreenH}
                {...extraProps}
            />
            {!!caption || !!credit ? <Caption {...{ caption, credit, creditLink }} /> : null}
        </>
    );
};

export { ResponsiveImage };
